// « Webhooks » tab of /commerce/orders: signed parcel.problem calls to the
// account's servers. Each endpoint is a row (state, secret hint, last test,
// last delivery, consecutive failures); « Tester » verifies it; what cuts the
// flow (new secret, pause, delete) asks first. Deliveries open under the row.

import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
    History, KeyRound, Pause, Play, Plus, ShieldCheck, Trash2, Webhook, Zap,
} from 'lucide-react';
import { CodeBlock, SecretReveal } from './ApiTab';
import { del, fail, formatWhen, get, patch, post } from './api';
import { hostOf } from './commerceAttention';
import {
    Badge, Banner, Card, ConfirmDialog, DescriptionList, EmptyState, OverflowMenu,
} from '../../design/ui';

const STATUS_TONE = { pending_verification: 'warning', active: 'success', disabled: undefined };
const DELIVERY_TONE = { delivered: 'success', failed: 'danger' };

const nodeVerify = `import crypto from 'node:crypto';

// app.post('/webhooks/unanimity', express.raw({ type: 'application/json' }), handler)
function verify(rawBody, header, secret) {
  const parts = Object.fromEntries(header.split(',').map((p) => p.split('=')));
  if (Math.abs(Date.now() / 1000 - Number(parts.t)) > 300) return false;
  const expected = crypto.createHmac('sha256', secret).update(\`\${parts.t}.\${rawBody}\`).digest('hex');
  const want = Buffer.from(expected);
  const given = Buffer.from(parts.v1 || '');
  return given.length === want.length && crypto.timingSafeEqual(want, given);
}`;

const pythonVerify = `import hashlib, hmac, time

def verify(raw_body: bytes, header: str, secret: str) -> bool:
    parts = dict(p.split("=", 1) for p in header.split(","))
    if abs(time.time() - int(parts["t"])) > 300:
        return False
    expected = hmac.new(secret.encode(), f"{parts['t']}.".encode() + raw_body, hashlib.sha256).hexdigest()
    return hmac.compare_digest(expected, parts.get("v1", ""))`;

const examplePayload = `{
  "id": "evt_66f…_stall",
  "type": "parcel.problem",
  "created_at": "2026-10-05T14:02:11Z",
  "data": {
    "parcel": {
      "tracking_number": "6A12345678901",
      "carrier": "Colissimo",
      "status": "in_transit",
      "problem": {"kind": "stall", "label": "…", "detail": null, "detected_at": "…"},
      "last_event": {"at": "…", "text": "En cours d'acheminement"}
    },
    "orders": [{"id": "…", "source": "shopify", "external_id": "4501", "number": "#1001", "client_ref": null}]
  }
}`;

const TestOutcome = ({ result }) => {
    const { t } = useTranslation();
    if (!result) return null;
    return (
        <Banner tone={result.ok ? 'success' : 'danger'}>
            {result.ok
                ? t('commerceOrders.testOk', 'Connexion vérifiée : HTTP {{code}} en {{ms}} ms. Le webhook est actif.', { code: result.status_code, ms: result.duration_ms })
                : t('commerceOrders.testFailed', 'Échec du test : {{error}}', { error: result.error || `HTTP ${result.status_code}` })}
            {result.response_excerpt && <pre className="co-v2__excerpt">{result.response_excerpt}</pre>}
        </Banner>
    );
};

const Deliveries = ({ endpointId }) => {
    const { t, i18n } = useTranslation();
    const lang = i18n.language || 'fr';
    const [items, setItems] = useState(null);
    useEffect(() => {
        get(`/webhooks/${endpointId}/deliveries`).then((res) => setItems(res.data.items || [])).catch(() => setItems([]));
    }, [endpointId]);
    if (items === null) return <div className="co-v2__skeleton" aria-hidden="true"><span className="u-skeleton" /></div>;
    if (items.length === 0) return <p className="u-help">{t('commerceOrders.noDelivery', 'Aucun événement envoyé pour l’instant : ils partent dès qu’un colis a un problème.')}</p>;
    return (
        <div className="u-table-scroll co-v2__deliveries">
            <table className="u-table">
                <thead>
                    <tr>
                        <th style={{ width: '32%' }}>{t('commerceOrders.deliveryEvent', 'Événement')}</th>
                        <th style={{ width: '34%' }}>{t('commerceOrders.deliveryStatus', 'État')}</th>
                        <th style={{ width: '10%' }} className="co-v2__num">{t('commerceOrders.deliveryAttempts', 'Essais')}</th>
                        <th style={{ width: '24%' }}>{t('commerceOrders.deliveryWhen', 'Créé')}</th>
                    </tr>
                </thead>
                <tbody>
                    {items.map((d) => (
                        <tr key={d.id}>
                            <td className="co-v2__mono" title={d.event_id}>{d.event_type}</td>
                            <td title={d.last_error || ''}>
                                <Badge tone={DELIVERY_TONE[d.status]}>{t(`commerceOrders.deliveryStatuses.${d.status}`, d.status)}</Badge>
                                {d.last_error && d.status !== 'delivered' && <span className="u-cell-sub">{d.last_error}</span>}
                            </td>
                            <td className="co-v2__num">{d.attempts}</td>
                            <td>{formatWhen(d.created_at, lang)}</td>
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );
};

const EndpointRow = ({ endpoint, isAdmin, onChanged }) => {
    const { t, i18n } = useTranslation();
    const lang = i18n.language || 'fr';
    const [busy, setBusy] = useState('');
    const [result, setResult] = useState(null);
    const [secret, setSecret] = useState(null);
    const [error, setError] = useState('');
    const [confirm, setConfirm] = useState(null); // 'rotate' | 'pause' | 'delete'
    const [showDeliveries, setShowDeliveries] = useState(false);
    const host = hostOf(endpoint.url);

    const run = async (kind, fn) => {
        setBusy(kind);
        setError('');
        try {
            await fn();
        } catch (err) {
            setError(fail(err, t('commerceOrders.actionError', 'L’opération a échoué.')));
        } finally {
            setBusy('');
            setConfirm(null);
        }
    };

    const test = () => run('test', async () => { const res = await post(`/webhooks/${endpoint.id}/test`); setResult(res.data); onChanged(); });
    const rotate = () => run('rotate', async () => { const res = await post(`/webhooks/${endpoint.id}/rotate-secret`); setSecret(res.data.secret); setResult(null); onChanged(); });
    const toggle = () => run('toggle', async () => { await patch(`/webhooks/${endpoint.id}`, { enabled: endpoint.status === 'disabled' }); setResult(null); onChanged(); });
    const remove = () => run('delete', async () => { await del(`/webhooks/${endpoint.id}`); onChanged(); });

    const disabled = endpoint.status === 'disabled';
    const lastTest = endpoint.last_test;
    const lastDelivery = endpoint.last_delivery;
    const failing = Number(endpoint.consecutive_failures) > 0;

    const confirmations = {
        rotate: {
            title: t('ordersMoneyUi.webhooks.rotateTitle', 'Régénérer le secret de {{host}} ?', { host }),
            message: t('ordersMoneyUi.webhooks.rotateMessage', 'L’ancien secret cesse de fonctionner tout de suite : les envois s’arrêtent jusqu’à ce que votre application charge le nouveau secret et que vous cliquiez « Tester ».'),
            confirmLabel: t('commerceOrders.rotate', 'Régénérer le secret'),
            onConfirm: rotate,
        },
        pause: {
            title: t('ordersMoneyUi.webhooks.pauseTitle', 'Mettre en pause {{host}} ?', { host }),
            message: t('ordersMoneyUi.webhooks.pauseMessage', 'Plus aucun problème colis n’est envoyé à cette adresse. Le réactiver demande un nouveau test.'),
            confirmLabel: t('commerceOrders.pause', 'Mettre en pause'),
            onConfirm: toggle,
        },
        delete: {
            title: t('ordersMoneyUi.webhooks.deleteTitle', 'Supprimer le webhook {{host}} ?', { host }),
            message: t('ordersMoneyUi.webhooks.deleteMessage', 'Votre serveur ne recevra plus les problèmes colis à cette adresse. Cette action est irréversible.'),
            confirmLabel: t('commerceOrders.confirmDelete', 'Supprimer définitivement'),
            onConfirm: remove,
        },
    };

    return (
        <li className="co-v2__endpoint">
            <div className="u-row">
                <span className={`u-icon-chip u-icon-chip--sm u-icon-chip--${failing ? 'danger' : disabled ? 'neutral' : endpoint.status === 'active' ? 'success' : 'warning'}`} aria-hidden="true">
                    <Webhook strokeWidth={1.8} />
                </span>
                <div className="u-row__main">
                    <div className="u-row__title">
                        <span className="co-v2__url" title={endpoint.url}>{endpoint.url}</span>
                        <Badge tone={STATUS_TONE[endpoint.status]} dot={endpoint.status !== 'disabled'}>
                            {t(`commerceOrders.webhookStatuses.${endpoint.status}`, endpoint.status)}
                        </Badge>
                        {failing && (
                            <Badge tone="danger">
                                {t('ordersMoneyUi.webhooks.failures', '{{count}} échecs consécutifs', { count: endpoint.consecutive_failures })}
                            </Badge>
                        )}
                    </div>
                    <div className="u-row__meta u-row__meta--wrap">
                        {[
                            endpoint.description,
                            `${t('commerceOrders.secret', 'Secret')} ${endpoint.secret_hint || '—'}`,
                            `${t('commerceOrders.lastTest', 'Dernier test')} : ${lastTest ? `${formatWhen(lastTest.at, lang)} · ${lastTest.ok ? 'OK' : (lastTest.error || `HTTP ${lastTest.status_code}`)}` : '—'}`,
                            `${t('commerceOrders.lastDelivery', 'Dernier envoi')} : ${lastDelivery ? `${formatWhen(lastDelivery.at, lang)} · ${lastDelivery.ok ? `HTTP ${lastDelivery.status_code}` : (lastDelivery.error || `HTTP ${lastDelivery.status_code}`)}` : '—'}`,
                        ].filter(Boolean).join(' · ')}
                    </div>
                </div>
                <div className="u-row__side">
                    {isAdmin && (
                        <button type="button" className="u-btn u-btn--secondary u-btn--sm" onClick={test} disabled={Boolean(busy) || disabled}>
                            <Zap aria-hidden="true" />
                            {busy === 'test' ? t('commerceOrders.testing', 'Test…') : t('commerceOrders.test', 'Tester')}
                        </button>
                    )}
                    <button type="button" className="u-btn u-btn--ghost u-btn--sm" onClick={() => setShowDeliveries(!showDeliveries)} aria-expanded={showDeliveries}>
                        <History aria-hidden="true" />
                        {showDeliveries ? t('commerceOrders.hideDeliveries', 'Masquer les envois') : t('commerceOrders.showDeliveries', 'Voir les envois')}
                    </button>
                    {isAdmin && (
                        <OverflowMenu
                            label={t('ordersMoneyUi.moreActions', 'Plus d’actions')}
                            items={[
                                { label: t('commerceOrders.rotate', 'Régénérer le secret'), icon: KeyRound, onClick: () => setConfirm('rotate'), disabled: Boolean(busy) },
                                disabled
                                    ? { label: t('commerceOrders.enable', 'Réactiver'), icon: Play, onClick: toggle, disabled: Boolean(busy) }
                                    : { label: t('commerceOrders.pause', 'Mettre en pause'), icon: Pause, onClick: () => setConfirm('pause'), disabled: Boolean(busy) },
                                { separator: true },
                                { label: t('commerceOrders.delete', 'Supprimer'), icon: Trash2, danger: true, onClick: () => setConfirm('delete'), disabled: Boolean(busy) },
                            ]}
                        />
                    )}
                </div>
            </div>
            {(endpoint.status === 'pending_verification' || secret || result || error || showDeliveries) && (
                <div className="co-v2__endpoint-body">
                    {endpoint.status === 'pending_verification' && !secret && (
                        <p className="u-help">{t('commerceOrders.pendingHint', 'Configurez le secret dans votre application, puis cliquez « Tester » : le webhook reçoit les événements dès qu’il répond 2xx.')}</p>
                    )}
                    {secret && (
                        <SecretReveal title={t('commerceOrders.newSecret', 'Nouveau secret')} value={secret} onDone={() => setSecret(null)}
                            hint={t('commerceOrders.secretOnce', 'Copiez-le dans votre application maintenant : il ne sera plus affiché. Puis cliquez « Tester » pour réactiver les envois.')} />
                    )}
                    <TestOutcome result={result} />
                    {error && <Banner tone="danger">{error}</Banner>}
                    {showDeliveries && <Deliveries endpointId={endpoint.id} />}
                </div>
            )}
            {confirm && (
                <ConfirmDialog
                    {...confirmations[confirm]}
                    cancelLabel={t('commerceOrders.cancel', 'Annuler')}
                    onCancel={() => setConfirm(null)}
                    busy={Boolean(busy)}
                />
            )}
        </li>
    );
};

const WebhooksTab = ({ isAdmin, onChanged }) => {
    const { t } = useTranslation();
    const [data, setData] = useState(null);
    const [form, setForm] = useState({ url: '', description: '' });
    const [created, setCreated] = useState(null);
    const [error, setError] = useState('');
    const [busy, setBusy] = useState(false);

    const load = useCallback(async () => {
        try {
            const res = await get('/webhooks');
            setData(res.data);
        } catch (err) {
            setError(fail(err, t('commerceOrders.webhooksError', 'Impossible de charger les webhooks.')));
            setData({ items: [] });
        }
    }, [t]);

    useEffect(() => { load(); }, [load]);

    const changed = () => { load(); if (onChanged) onChanged(); };

    const create = async (e) => {
        e.preventDefault();
        setBusy(true);
        setError('');
        try {
            const res = await post('/webhooks', { url: form.url.trim(), description: form.description.trim() || null, events: ['parcel.problem'] });
            setCreated(res.data);
            setForm({ url: '', description: '' });
            changed();
        } catch (err) {
            setError(fail(err, t('commerceOrders.webhookCreateError', 'Impossible d’ajouter le webhook.')));
        } finally {
            setBusy(false);
        }
    };

    const items = data?.items || [];

    return (
        <div className="co-v2__stack">
            <Card
                icon={Webhook}
                title={t('commerceOrders.webhooksTitle', 'Webhooks')}
                badge={data ? <Badge tone={items.length ? 'accent' : undefined}>{items.length}</Badge> : null}
                description={t('commerceOrders.webhooksIntro', 'Recevez un appel signé sur votre serveur dès qu’un de vos colis a un problème (retard anormal, colis silencieux, incident transporteur, perte présumée).')}
                flush
                footerNote={isAdmin ? null : t('commerceOrders.adminOnlyWebhooks', 'Seul un administrateur peut ajouter ou modifier des webhooks.')}
                footer={isAdmin ? (
                    <form className="co-v2__inline-form co-v2__inline-form--webhook" onSubmit={create}>
                        <input className="u-input" type="url" value={form.url} onChange={(e) => setForm({ ...form, url: e.target.value })}
                            placeholder="https://api.votre-domaine.fr/webhooks/unanimity" required
                            aria-label={t('ordersMoneyUi.webhooks.url', 'Adresse HTTPS du webhook')} />
                        <input className="u-input" type="text" value={form.description} maxLength={200} onChange={(e) => setForm({ ...form, description: e.target.value })}
                            placeholder={t('commerceOrders.webhookDescription', 'Description (facultatif)')}
                            aria-label={t('commerceOrders.webhookDescription', 'Description (facultatif)')} />
                        <button type="submit" className="u-btn u-btn--primary" disabled={busy || !form.url.trim().startsWith('https://')}>
                            <Plus aria-hidden="true" />
                            {t('ordersMoneyUi.webhooks.add', 'Ajouter un webhook')}
                        </button>
                    </form>
                ) : null}
            >
                {(error || created) && (
                    <div className="co-v2__pad co-v2__stack-sm">
                        {error && <Banner tone="danger">{error}</Banner>}
                        {created && (
                            <SecretReveal title={t('commerceOrders.webhookCreated', 'Webhook ajouté — votre secret de signature')} value={created.secret}
                                hint={t('commerceOrders.webhookSecretHint', 'Enregistrez ce secret dans votre application (il ne sera plus affiché), vérifiez la signature de chaque appel, puis cliquez « Tester » sur le webhook.')}
                                onDone={() => setCreated(null)} />
                        )}
                    </div>
                )}
                {data === null && <div className="co-v2__skeleton" aria-hidden="true"><span className="u-skeleton" /><span className="u-skeleton" /></div>}
                {data && items.length === 0 && (
                    <EmptyState icon={Webhook} title={t('commerceOrders.noWebhook', 'Aucun webhook pour l’instant.')}>
                        {t('ordersMoneyUi.webhooks.emptyHint', 'Ajoutez l’adresse HTTPS de votre serveur : il reçoit un appel signé à chaque problème colis.')}
                    </EmptyState>
                )}
                {items.length > 0 && (
                    <ul className="u-list co-v2__endpoints">
                        {items.map((endpoint) => (
                            <EndpointRow key={endpoint.id} endpoint={endpoint} isAdmin={isAdmin} onChanged={changed} />
                        ))}
                    </ul>
                )}
            </Card>

            <Card
                icon={ShieldCheck}
                iconTone="neutral"
                title={t('commerceOrders.verifyTitle', 'Vérifier les appels')}
                description={t('commerceOrders.verifyIntro', 'Chaque appel est un POST JSON signé. L’en-tête Unanimity-Signature vaut t=<horodatage>,v1=<HMAC-SHA256 hexadécimal du texte « t.corps brut » avec votre secret>. Refusez un appel dont la signature ne correspond pas ou dont l’horodatage a plus de 5 minutes, et répondez 2xx en moins de 10 secondes : sinon l’envoi est retenté (8 essais sur 24 h). Les redirections ne sont pas suivies.')}
            >
                <div className="co-v2__docs">
                    <DescriptionList
                        items={[
                            { label: t('commerceOrders.headers', 'En-têtes'), value: <span className="co-v2__mono">Unanimity-Signature · Unanimity-Event-Id · Unanimity-Event-Type</span> },
                            { label: t('commerceOrders.events', 'Événements'), value: <><span className="co-v2__mono">parcel.problem · webhook.test</span> ({t('commerceOrders.testEventNote', 'bouton Tester')})</> },
                            { label: t('commerceOrders.idempotency', 'Doublons'), value: t('commerceOrders.idempotencyValue', 'un même événement peut arriver deux fois après une reprise : dédoublonnez sur son id.') },
                        ]}
                    />
                    <CodeBlock label={t('commerceOrders.examplePayload', 'Exemple — parcel.problem')} code={examplePayload} />
                    <CodeBlock label="Node.js" code={nodeVerify} />
                    <CodeBlock label="Python" code={pythonVerify} />
                </div>
            </Card>
        </div>
    );
};

export default WebhooksTab;
